import { useEffect, useRef } from 'react'
import { FindBarChrome } from '@/components/features/find-bar-chrome'
import { focusAndSelectFindInput } from '@/lib/focus-find-input'
import { testIds } from '@/lib/test-ids'

interface PreviewFindBarProps {
  query: string
  current: number
  total: number
  onQueryChange: (value: string) => void
  onNext: () => void
  onPrev: () => void
  onClose: () => void
  focusRequestId?: number
}

export function PreviewFindBar({
  query,
  current,
  total,
  onQueryChange,
  onNext,
  onPrev,
  onClose,
  focusRequestId = 0,
}: PreviewFindBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      focusAndSelectFindInput(inputRef.current)
    })
    return () => window.cancelAnimationFrame(id)
  }, [focusRequestId])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isMod = e.metaKey || e.ctrlKey
    if (e.key === 'Enter' || (isMod && e.key.toLowerCase() === 'g')) {
      e.preventDefault()
      if (e.shiftKey) onPrev()
      else onNext()
    } else if (isMod && e.key.toLowerCase() === 'f') {
      e.preventDefault()
      focusAndSelectFindInput(e.currentTarget)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  const hasQuery = query.trim().length > 0
  const noResults = hasQuery && total === 0
  const matchLabel = hasQuery
    ? total === 0
      ? 'No results'
      : `${current + 1} of ${total}`
    : ''

  return (
    <div className="absolute top-2 right-3 z-20 pointer-events-auto">
      <FindBarChrome
        inputRef={inputRef}
        query={query}
        matchLabel={matchLabel}
        noResults={noResults}
        prevDisabled={!hasQuery || total === 0}
        nextDisabled={!hasQuery || total === 0}
        onQueryChange={onQueryChange}
        onKeyDown={handleKeyDown}
        onPrev={onPrev}
        onNext={onNext}
        onClose={onClose}
        prevTitle="Previous match (Shift+Enter)"
        nextTitle="Next match (Enter)"
        testIds={testIds.documentPreviewFind}
      />
    </div>
  )
}
